import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { Navigate, useBlocker } from "react-router-dom";
import { PermissionItem } from "@/api/__generated__/orval/auth";
import { useAIChat } from "@/hooks";
import { useRolePermission } from "@/hooks/rolePermission/useRolePermission";
import { ModalBriefMode } from "@/modals/openBrief/ModalBriefMode";
import { ModalCancelBrief } from "@/modals/openBrief/ModalCancelBrief/ModalCancelBrief";
import {
  allowBriefSubmitNavigationAtom,
  currentAccountAtom,
  currentWorkspaceAtom,
  isBriefWithAIAtom,
  openBriefAITermsAcceptedAtom,
  userAtom,
} from "@/store";
import { AIChat } from "./AIChat";
import { BriefSummary } from "./BriefSummary";
import {
  type FieldValues,
  type InitialAIValuesType,
} from "./BriefSummary/BriefSummary.types";

export const OpenBriefPage = () => {
  const [initialAIValues, setInitialAIValues] = useState<InitialAIValuesType>();
  const [latestFields, setLatestFields] = useState<FieldValues>([]);
  const [isDrawerFormOpen, setIsDrawerFormOpen] = useState(false);
  const [isModalBriefModeOpen, setIsModalBriefModeOpen] = useState(false);

  const currentUser = useAtomValue(userAtom);
  const currentAccount = useAtomValue(currentAccountAtom);
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const [isBriefWithAi, setIsBriefWithAi] = useAtom(isBriefWithAIAtom);
  const isTermsAccepted = useAtomValue(openBriefAITermsAcceptedAtom);
  const allowSubmitNavigation = useAtomValue(allowBriefSubmitNavigationAtom);
  const setAllowSubmitNavigation = useSetAtom(allowBriefSubmitNavigationAtom);

  const { userHasPermission } = useRolePermission();
  const aiChatTools = useAIChat();

  const hasBriefResponse = initialAIValues !== undefined;
  const shouldBlockNavigation = hasBriefResponse && !allowSubmitNavigation;
  const blocker = useBlocker(shouldBlockNavigation);

  useEffect(() => {
    if (initialAIValues === undefined) {
      setAllowSubmitNavigation(false);
    }
  }, [initialAIValues, setAllowSubmitNavigation]);

  useEffect(() => {
    if (!isTermsAccepted) {
      setIsModalBriefModeOpen(true);
    } else {
      setIsBriefWithAi(true);
    }
  }, [isTermsAccepted]);

  useEffect(() => {
    if (aiChatTools.initialAIValues !== undefined) {
      setInitialAIValues(aiChatTools.initialAIValues);
      setIsDrawerFormOpen(true);
    } else {
      setInitialAIValues(undefined);
      setIsDrawerFormOpen(false);
    }
  }, [aiChatTools.initialAIValues]);

  const handleGlobalReset = () => {
    setAllowSubmitNavigation(true);
    aiChatTools.resetChat();
    setInitialAIValues(undefined);
    setLatestFields([]);
    setIsDrawerFormOpen(false);
  };

  if (!currentUser || !currentAccount || !currentWorkspace?.id) {
    return null;
  }

  if (
    !userHasPermission(PermissionItem.omsordersmanagement) &&
    !userHasPermission(PermissionItem.omsbriefsmanagement)
  ) {
    return <Navigate to="/404" replace />;
  }

  return (
    <div className="w-full h-full max-w-full max-h-full flex justify-center">
      <div className="flex w-[100%] max-w-[700px] mx-auto">
        <AIChat
          setInitialAIValues={setInitialAIValues}
          latestFields={latestFields}
          aiChatTools={aiChatTools}
        />
      </div>

      {isDrawerFormOpen && isBriefWithAi && (
        <div className="flex w-[40%] max-w-[40%] min-w-[400px]">
          <BriefSummary
            initialAIValues={initialAIValues}
            onFieldsChange={setLatestFields}
            onReset={handleGlobalReset}
          />
        </div>
      )}

      <ModalBriefMode
        isOpen={isModalBriefModeOpen}
        onOpenChange={() => setIsModalBriefModeOpen(false)}
      />

      {blocker.state === "blocked" && (
        <ModalCancelBrief
          isOpen
          onOpenChange={() => blocker.reset()}
          onConfirmExit={() => blocker.proceed()}
          onReset={handleGlobalReset}
        />
      )}
    </div>
  );
};
